import * as React from 'react'
import { Search } from 'lucide-react'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import { SortableTableHead } from '@/components/ui/table'
import { displayMonthDay } from '@/lib/dates'

export type StageKey =
  | 'needs_decision'
  | 'upcoming'
  | 'in_grace'
  | 'lapsing_within_7_days'
  | 'recently_lapsed'
export type PipelineSortKey = 'member' | 'status' | 'level' | 'open' | 'due' | 'next' | 'autopay'
export interface PipelineSort {
  key: PipelineSortKey
  direction: 'ascending' | 'descending'
}

export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState(value)
  React.useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timeout)
  }, [delay, value])
  return debounced
}

export function RenewalPipelineToolbar({
  search,
  onSearchChange,
  levelOptions,
  levels,
  onLevelsChange,
  autopayOptions,
  autopayStates,
  onAutopayStatesChange,
}: {
  search: string
  onSearchChange: (value: string) => void
  levelOptions: string[]
  levels: ReadonlySet<string>
  onLevelsChange: (values: Set<string>) => void
  autopayOptions: string[]
  autopayStates: ReadonlySet<string>
  onAutopayStatesChange: (values: Set<string>) => void
}) {
  return (
    <div className="mt-4 flex items-center gap-2">
      <div className="relative w-64">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <Input
          className="h-8 pl-9"
          value={search}
          placeholder="Search members"
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>
      <FilterDropdown
        label="Level"
        emptyLabel="All levels"
        options={levelOptions.map((level) => ({ value: level, label: level }))}
        selected={levels}
        onChange={onLevelsChange}
      />
      <FilterDropdown
        label="Autopay"
        emptyLabel="Any autopay"
        options={autopayOptions.map((value) => ({ value, label: autopayFilterLabel(value) }))}
        selected={autopayStates}
        onChange={onAutopayStatesChange}
      />
    </div>
  )
}

export function PipelineSortHead({
  column,
  sort,
  onSort,
  ...props
}: Omit<React.ComponentProps<typeof SortableTableHead>, 'active' | 'direction' | 'onToggle'> & {
  column: PipelineSortKey
  sort: PipelineSort | null
  onSort: (key: PipelineSortKey) => void
}) {
  return (
    <SortableTableHead
      {...props}
      active={sort?.key === column}
      direction={sort?.key === column ? sort.direction : 'ascending'}
      onToggle={() => onSort(column)}
    />
  )
}

export function nextPipelineSort(current: PipelineSort | null, key: PipelineSortKey): PipelineSort {
  if (current?.key !== key) return { key, direction: 'ascending' }
  return { key, direction: current.direction === 'ascending' ? 'descending' : 'ascending' }
}

export function renewalPipelineEndpoint({
  search,
  levels,
  autopayStates,
  stage,
  sort,
}: {
  search: string
  levels: ReadonlySet<string>
  autopayStates: ReadonlySet<string>
  stage: StageKey
  sort: PipelineSort | null
}) {
  const parameters = new URLSearchParams()
  if (search.trim()) parameters.set('search', search.trim())
  levels.forEach((level) => parameters.append('levels[]', level))
  autopayStates.forEach((state) => parameters.append('autopay_states[]', state))
  if (sort) {
    parameters.set('sort_stage', stage)
    parameters.set('sort', sort.key)
    parameters.set('direction', sort.direction)
  }
  const query = parameters.toString()
  return `/api/v1/renewal_pipeline${query ? `?${query}` : ''}`
}

export function pipelineDateHeading(stage: StageKey) {
  if (stage === 'upcoming' || stage === 'needs_decision') return 'Due'
  if (stage === 'recently_lapsed') return 'Lapsed'
  return 'Overdue since'
}

export function lapseForecast(row: { status: string; days_until_lapse: number | null }) {
  if (row.status === 'Lapsed') return 'Lapsed'
  if (row.days_until_lapse === null) return '—'
  if (row.days_until_lapse === 1) return 'Lapses tomorrow'
  return `Lapses in ${row.days_until_lapse} days`
}

export function displayPipelineStageDate(
  row: {
    renewal_due_on: string | null
    overdue_since_on: string | null
    lapsed_on: string | null
  },
  stage: StageKey,
) {
  const value = stageDate(row, stage)
  if (!value) return '—'
  return displayMonthDay(value)
}

function stageDate(
  row: {
    renewal_due_on: string | null
    overdue_since_on: string | null
    lapsed_on: string | null
  },
  stage: StageKey,
) {
  if (stage === 'recently_lapsed') return row.lapsed_on
  return stage === 'upcoming' || stage === 'needs_decision'
    ? row.renewal_due_on
    : row.overdue_since_on
}

function autopayFilterLabel(value: string) {
  const labels: Record<string, string> = {
    will_charge: 'Will charge',
    retry_scheduled: 'Retry scheduled',
    disabled: 'Disabled',
    not_enrolled: 'Not enrolled',
  }
  return labels[value] ?? value
}
